import ShelfController from '../../core/shelf.controller';
import dayjs from 'dayjs';
import { User } from '../../data';
import { canAccess } from '../../core/access';
import { downloadExcel, downloadExcelSheets } from '../../utils/excel';
import { fmtCapture } from '../../utils/timeUtils';
import { DEFAULT_FILTERS, SOURCE_LABELS, formatPriceName, filtersToQuery, isCanceled } from './levantamientoShared';

// Read the filters the list handed off through the URL query string.
function filtersFromSearch(sp) {
    // Guard against stale links carrying literal "undefined"/"null" strings.
    const val = (key) => {
        const v = sp.get(key);
        return v && v !== 'undefined' && v !== 'null' ? v : null;
    };
    // Comma-separated multi-select lists come back as strings — split them into arrays.
    const list = (key) => {
        const v = val(key);
        return v ? v.split(',').map((s) => s.trim()).filter(Boolean) : [];
    };
    // Wholesale mode is carried as the backend flags; fold them back into the single control value.
    const mayorista = val('onlyMayorista') === 'true' ? 'only' : val('excludeMayorista') === 'true' ? 'exclude' : 'all';
    const filters = {
        ...DEFAULT_FILTERS,
        source: list('source'),
        category: list('category'),
        segment: list('segment'),
        clientId: list('clientId'),
        storeId: list('storeId'),
        userId: list('userId'),
        userType: list('userType'),
        channel: list('channel'),
        tagId: list('tagId'),
        grams: list('grams').map(Number).filter((n) => !Number.isNaN(n)),
        storeType: list('storeType'),
        mayorista,
        search: val('search') || ''
    };
    const startDate = val('startDate');
    const endDate = val('endDate');
    const dateRange =
        startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : [dayjs().subtract(30, 'day'), dayjs()];
    return { filters, dateRange };
}

class LevantamientoChartsController extends ShelfController {
    async initialize() {
        if (!this.isAuthenticated) {
            this.setState({ loading: false });
            this.navigateLogin();
            return;
        }

        const user = User.getInstance();
        if (!canAccess(user?.userType, 'levantamiento-general')) {
            this.setState({ loading: false, accessDenied: true });
            return;
        }

        try {
            const { filters, dateRange } = filtersFromSearch(this.props.searchParams);

            const [clients, tagsRes, levFacets, storesRes, users] = await Promise.all([
                this.data.fetchClients().catch(() => []),
                this.data.fetchTags().catch(() => null),
                this.data.fetchLevantamientoFacets().catch(() => null),
                this.data.fetchStoresCached().catch(() => null),
                this.data.fetchUsers().catch(() => [])
            ]);

            const clientOptions = (clients || [])
                .filter((c) => c?._id)
                .map((c) => ({ label: c.name, value: c._id }));
            const storeOptions = (storesRes?.data?.stores || [])
                .filter((s) => s?._id)
                .map((s) => ({ label: s.name, value: s._id }));
            const userOptions = (users || [])
                .filter((u) => u?._id && u?.name)
                .map((u) => ({ label: u.name, value: u._id }));
            // Category/segment/grams/storeType all come from the viewer-gated levantamiento facets,
            // so asesores get the full filter set (no dependency on the admin-only audit facets).
            const categoryOptions = (levFacets?.data?.categories || [])
                .filter(Boolean)
                .map((c) => ({ label: c, value: c }));
            const segmentOptions = (levFacets?.data?.segments || [])
                .filter(Boolean)
                .map((s) => ({ label: s, value: s }));
            const tagOptions = (tagsRes?.tags || [])
                .filter((t) => t?._id && (t.applicableModels || []).some((m) => ['Client', 'User', 'Store'].includes(m)))
                .map((t) => ({ label: t.name, value: t._id, color: t.color }));
            const gramsOptions = (levFacets?.data?.grams || []).map((g) => ({ label: `${g} g`, value: g }));
            const storeTypeOptions = (levFacets?.data?.storeTypes || []).filter(Boolean).map((s) => ({ label: s, value: s }));

            this.setState({
                filters,
                dateRange,
                clientOptions,
                storeOptions,
                userOptions,
                categoryOptions,
                segmentOptions,
                tagOptions,
                gramsOptions,
                storeTypeOptions,
                loading: false
            });
            await this.loadChart();
        } catch (err) {
            this.setState({ loading: false, error: true });
        }
    }

    async loadChart() {
        // Abort the previous request so a fast filter change ends on the latest selection.
        this._chartAbort?.abort();
        const abort = new AbortController();
        this._chartAbort = abort;
        this.setState({ chartLoading: true });
        try {
            const { filters, dateRange } = this.state;
            const res = await this.data.fetchLevantamientoPriceBySku(filtersToQuery(filters, dateRange), abort.signal);
            if (this._chartAbort !== abort) return; // superseded
            this.setState({
                chartRows: res?.data?.rows || [],
                chartOverallAvg: res?.data?.overallAvg || 0,
                chartLoading: false
            });
        } catch (err) {
            if (this._chartAbort !== abort || isCanceled(err)) return;
            this.props.onMessage?.('error', 'No se pudo cargar el gráfico de precios');
            this.setState({ chartLoading: false });
        }
    }

    // Reload whichever tab's data is currently visible.
    reloadActive() {
        if (this.state.activeTab === 'tendencias') this.loadTrend();
        else if (this.state.activeTab === 'comparaciones') this.loadComparison();
        else this.loadChart();
    }

    async loadComparison() {
        const { filters, dateRange } = this.state;
        // The comparison is heavy and only meaningful within a category — require at least one.
        if (!filters.category?.length) {
            this._compSig = null;
            this.setState({ comparison: null, comparisonLoading: false });
            return;
        }
        const query = filtersToQuery(filters, dateRange);
        const sig = JSON.stringify(query);
        // Skip refetching when the filters haven't changed (e.g. just toggling tabs).
        if (this._compSig === sig && this.state.comparison) return;
        this._compSig = sig;
        this._compAbort?.abort();
        const abort = new AbortController();
        this._compAbort = abort;
        try {
            this.setState({ comparisonLoading: true });
            const res = await this.data.fetchLevantamientoComparison(query, abort.signal);
            if (this._compAbort !== abort) return; // superseded
            this.setState({ comparison: res?.data || null, comparisonLoading: false });
        } catch (err) {
            if (this._compAbort !== abort || isCanceled(err)) return;
            this._compSig = null;
            this.props.onMessage?.('error', 'No se pudo cargar la comparación de precios');
            this.setState({ comparisonLoading: false });
        }
    }

    setFilter(key, value) {
        this.setState((prev) => ({ filters: { ...prev.filters, [key]: value } }), () => this.reloadActive());
    }

    setDateRange(dateRange) {
        this.setState({ dateRange }, () => this.reloadActive());
    }

    async loadTrend() {
        const { filters, dateRange } = this.state;
        const query = filtersToQuery(filters, dateRange);
        const sig = JSON.stringify(query);
        if (this._trendSig === sig && this.state.trendSeries.length) return;
        this._trendSig = sig;
        this._trendAbort?.abort();
        const abort = new AbortController();
        this._trendAbort = abort;
        try {
            this.setState({ trendLoading: true });
            const res = await this.data.fetchLevantamientoPriceTrend(query, abort.signal);
            if (this._trendAbort !== abort) return; // superseded
            this.setState({
                trendSeries: res?.data?.series || [],
                trendWeeks: res?.data?.weeks || [],
                trendTruncated: !!res?.data?.truncated,
                trendLoading: false
            });
        } catch (err) {
            if (this._trendAbort !== abort || isCanceled(err)) return;
            this.props.onMessage?.('error', 'No se pudo cargar la tendencia de precios');
            this.setState({ trendLoading: false });
        }
    }

    // Click a line → that product's full weekly history (ignores the date-range filter).
    async openTrendDrill(product) {
        this.setState({ trendDrillProduct: product, trendDrillPoints: [], trendDrillLoading: true }, () => {
            this.trendDrillRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        try {
            const { filters } = this.state;
            // Pin by barcode (stable identity) when the product has one, so the history catches
            // every capture regardless of name drift; else fall back to the name tuple.
            const pin = product.barcode
                ? { barcode: product.barcode }
                : {
                      productName: product.productName,
                      ...(product.segment ? { segment: product.segment } : {}),
                      ...(product.grams != null ? { grams: product.grams } : {})
                  };
            const res = await this.data.fetchLevantamientoPriceTrend({
                ...filtersToQuery(filters, null),
                ...pin
            });
            this.setState({ trendDrillPoints: res?.data?.series?.[0]?.points || [], trendDrillLoading: false });
        } catch (err) {
            this.props.onMessage?.('error', 'No se pudo cargar el historial del producto');
            this.setState({ trendDrillLoading: false });
        }
    }

    closeTrendDrill() {
        this.setState({ trendDrillProduct: null, trendDrillPoints: [] });
    }

    onTabChange(key) {
        this.setState({ activeTab: key }, () => {
            if (key === 'tendencias') this.loadTrend();
            else if (key === 'comparaciones') this.loadComparison();
        });
    }

    // Export the two comparison tables to a two-sheet Excel file.
    exportComparison() {
        const c = this.state.comparison;
        if (!c?.mary?.length) {
            this.props.onMessage?.('warning', 'No hay datos de comparación para exportar');
            return;
        }
        const { mary, competition, compByGrams = {}, maryOverallAvg = 0, compOverallAvg = 0 } = c;
        const gk = (g) => (g == null ? '' : String(g));
        const r2 = (n) => Math.round(n * 100) / 100;

        // Sheet 1 — summary (same-grams PromComp).
        const summaryCols = ['Producto', 'Prom. Mary (USD)', 'Prom. Comp. (USD)', 'Índice (%)', 'Dif. (USD)'];
        const summaryItems = mary.map((m) => {
            const pc = compByGrams[gk(m.grams)] ?? null;
            return {
                Producto: formatPriceName(m),
                'Prom. Mary (USD)': m.avgUsd,
                'Prom. Comp. (USD)': pc,
                'Índice (%)': pc ? Math.round((m.avgUsd / pc - 1) * 100) : null,
                'Dif. (USD)': pc == null ? null : r2(m.avgUsd - pc)
            };
        });
        summaryItems.push({
            Producto: 'Total',
            'Prom. Mary (USD)': maryOverallAvg,
            'Prom. Comp. (USD)': compOverallAvg,
            'Índice (%)': compOverallAvg ? Math.round((maryOverallAvg / compOverallAvg - 1) * 100) : null,
            'Dif. (USD)': r2(maryOverallAvg - compOverallAvg)
        });

        // Sheet 2 — matrix (% difference of each Mary product vs each competitor).
        const maryLabels = mary.map((m) => formatPriceName(m));
        const matrixCols = ['Competidor', 'Precio Comp. (USD)', ...maryLabels];
        const matrixItems = competition.map((cp) => {
            const row = { Competidor: formatPriceName(cp), 'Precio Comp. (USD)': cp.avgUsd ?? null };
            mary.forEach((m, i) => {
                row[maryLabels[i]] = cp.avgUsd ? Math.round((m.avgUsd / cp.avgUsd - 1) * 100) : null;
            });
            return row;
        });
        const totalRow = { Competidor: 'Total (vs. competencia)', 'Precio Comp. (USD)': null };
        mary.forEach((m, i) => {
            const pc = compByGrams[gk(m.grams)] ?? null;
            totalRow[maryLabels[i]] = pc ? Math.round((m.avgUsd / pc - 1) * 100) : null;
        });
        matrixItems.push(totalRow);

        downloadExcelSheets(
            [
                { name: 'Resumen', columns: summaryCols, items: summaryItems },
                { name: 'Diferencia por producto', columns: matrixCols, items: matrixItems }
            ],
            `Comparacion_Index_${dayjs().format('YYYYMMDD')}.xlsx`
        );
    }

    // Drill down into a single SKU: list the individual captures behind its average
    // in a table below the chart.
    async openDrill(point) {
        this.setState({ drillSku: point, drillRows: [], drillLoading: true }, () => {
            this.drillRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        try {
            const { filters, dateRange } = this.state;
            // Pin by barcode (stable identity) when the product has one so the captures match the
            // point exactly, even across name drift; else pin the (name + segment + grams) tuple.
            const pin = point.barcode
                ? { barcode: point.barcode }
                : {
                      productName: point.productName,
                      ...(point.segment ? { segment: point.segment } : {}),
                      ...(point.grams != null ? { grams: point.grams } : {})
                  };
            const res = await this.data.fetchLevantamientoGeneral({
                ...filtersToQuery(filters, dateRange),
                ...pin,
                all: 'true'
            });
            this.setState({ drillRows: res?.data?.rows || [], drillLoading: false });
        } catch (err) {
            this.props.onMessage?.('error', 'No se pudieron cargar los precios de este producto');
            this.setState({ drillLoading: false });
        }
    }

    closeDrill() {
        this.setState({ drillSku: null, drillRows: [] });
    }

    // Filename-safe slug from a product's display name.
    _productSlug(product) {
        return formatPriceName(product).replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'producto';
    }

    // Export the captures behind one SKU (the click-through drill table).
    exportDrill() {
        const { drillSku, drillRows } = this.state;
        if (!drillRows?.length) {
            this.props.onMessage?.('warning', 'No hay precios para exportar');
            return;
        }
        const columns = [
            'Fuente',
            'Precio (USD)',
            'Atípico',
            'Piso de venta',
            'Tipo de tienda',
            'Cliente',
            'Usuario',
            'Rol',
            'Fecha'
        ];
        const data = drillRows.map((r) => ({
            Fuente: SOURCE_LABELS[r.source] || r.source,
            'Precio (USD)': r.priceUsd ?? '',
            Atípico: r.isOutlier ? 'Sí' : 'No',
            'Piso de venta': r.storeName,
            'Tipo de tienda': r.storeType || '',
            Cliente: r.clientName,
            Usuario: r.userName || '',
            Rol: r.userType || '',
            Fecha: r.capturedAt ? fmtCapture(r.capturedAt, r.source, 'YYYY-MM-DD HH:mm') : ''
        }));
        downloadExcel(columns, data, `SKU_${this._productSlug(drillSku)}_${dayjs().format('YYYYMMDD')}.xlsx`);
    }

    // Export one product's full weekly history (the trend click-through drill table).
    exportTrendDrill() {
        const { trendDrillProduct, trendDrillPoints } = this.state;
        if (!trendDrillPoints?.length) {
            this.props.onMessage?.('warning', 'No hay historial para exportar');
            return;
        }
        const columns = ['Semana', 'Precio promedio (USD)', '# precios'];
        const data = trendDrillPoints.map((p) => ({
            Semana: dayjs(p.week).format('YYYY-MM-DD'),
            'Precio promedio (USD)': p.avgUsd,
            '# precios': p.count
        }));
        downloadExcel(columns, data, `Historial_${this._productSlug(trendDrillProduct)}_${dayjs().format('YYYYMMDD')}.xlsx`);
    }

    goBack() {
        const { filters, dateRange } = this.state;
        const q = new URLSearchParams(filtersToQuery(filters, dateRange)).toString();
        this.props.navigate(`/dashboard/levantamiento-general${q ? `?${q}` : ''}`);
    }
}

export default LevantamientoChartsController;
